import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ExitToApp, Home } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { updateNotificationPreferencesThunk } from 'redux/slices/miscSlice';
import useHandleTrackEvent from '@/hooks/useHandleTrackEvent';

import ThemeModeToggler from '@/components/@2026/ThemeModeToggler';
import ThemedLogo from '@/components/ThemedLogo';
import { checkSession, logoutUser, resetUIState } from 'redux/slices/authSlice';

const Topbar = ({ onSidebarOpen }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const [anchorEl, setAnchorEl] = useState(null);
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const handleTrackEvent = useHandleTrackEvent();

  const handleProfileMenuClick = (event) => {
    if (anchorEl) setAnchorEl(null);
    else setAnchorEl(event.target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    if (localStorage.getItem('notificationToken')) {
      try {
        await dispatch(
          updateNotificationPreferencesThunk({ pushnotification: false }),
        ).unwrap();
        localStorage.removeItem('notificationToken');
      } catch (_e) {
        /* noop */
      }
    }
    dispatch(logoutUser());
    dispatch(resetUIState());
    dispatch(checkSession());
  };

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      width={1}
    >
      <ThemedLogo variant="default" height={{ xs: 40, md: 50 }} />
      <Box sx={{ display: 'flex' }} alignItems={'center'} gap={2}>
        <ThemeModeToggler />
        {isLoggedIn && userInfo ? (
          <Box>
            <Button
              data-trackid={'open_profile_menu'}
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={(e) => {
                handleTrackEvent(e, 'open_profile_menu');
                handleProfileMenuClick(e);
              }}
            >
              <Avatar
                alt={userInfo?.firstName?.toUpperCase()}
                src={userDetails?.profilePicture?.url}
              >
                {userInfo?.firstName?.[0]?.toUpperCase()}
              </Avatar>
            </Button>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              disableScrollLock={true}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              style={{
                marginTop: '15px',
              }}
            >
              <MenuItem
                data-trackid={'profile_menu_home_option'}
                onClick={(e) => {
                  handleTrackEvent(e, 'profile_menu_home_option');
                  history.push('/');
                }}
              >
                <Home style={{ marginRight: '10px' }} />
                Home
              </MenuItem>
              <MenuItem
                data-trackid={'profile_menu_logout_option'}
                onClick={(e) => {
                  handleTrackEvent(e, 'profile_menu_logout_option');
                  handleLogoutClick();
                }}
              >
                <ExitToApp style={{ marginRight: '10px' }} />
                Logout
              </MenuItem>
              <MenuItem
                style={{
                  color: 'rgba(0, 0, 0, 0.54)',
                  pointerEvents: 'none',
                }}
              >
                {userInfo?.email}
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box
            sx={{ display: { xs: 'none', md: 'flex' } }}
            alignItems={'center'}
          >
            <Box marginLeft={2}>
              <Button
                data-trackid={'CTA_sign_in'}
                size={'small'}
                variant="outlined"
                fullWidth
                component="a"
                href="/auth/signin"
                onClick={(e) => {
                  handleTrackEvent(e, 'CTA_sign_in');
                }}
              >
                Sign In
              </Button>
            </Box>
            <Box marginLeft={2}>
              <Button
                data-trackid={'CTA_sign_up'}
                size={'small'}
                variant="contained"
                fullWidth
                component="a"
                href="/auth/signup"
                onClick={(e) => {
                  handleTrackEvent(e, 'CTA_sign_up');
                }}
              >
                Sign Up
              </Button>
            </Box>
          </Box>
        )}
        <Box
          sx={{ display: { xs: 'block', md: 'none' } }}
          alignItems={'center'}
        >
          <Button
            data-trackid={'toggle_sidebar'}
            onClick={(e) => {
              handleTrackEvent(e, 'toggle_sidebar');
              onSidebarOpen();
            }}
            aria-label="Menu"
            variant={'outlined'}
            sx={{
              borderRadius: 2,
              minWidth: 'auto',
              padding: 1,
              borderColor: alpha(theme.palette.divider, 0.2),
            }}
          >
            <MenuIcon />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func,
};

export default Topbar;
